import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  QrCodeScannerRounded as ScanIcon,
  Inventory2Rounded as StockIcon,
  SaveRounded as SaveIcon,
  RestartAltRounded as ResetIcon,
  AssessmentRounded as ReportIcon,
  DeleteOutlineRounded as DeleteIcon,
  PersonRounded as PersonIcon,
  CheckCircleRounded as CheckIcon,
  ClearRounded as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import ReceiptReport from "./ReceiptReport";
import BarcodeScannerDialog from "./BarcodeScannerDialog";
import { reportPageSx, reportFixedSx } from "./reportLayout";
import PawnNavButtons from "./pawnNavButtons";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens (shared with the pawn report screens) ──────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const SUCCESS = "#059669";
const SUCCESS_PALE = "#ECFDF5";
const AMBER = "#B45309";

// 12-hour date-time with AM/PM, e.g. "17 Jul 2026, 03:42 PM".
const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : "—";
};
const fmtDate = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};
const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

// A single "who + when" line, e.g. "Radhika Menon (ID 12)".
const personLabel = (person) => {
  if (!person) return "—";
  const name = person.name || "--";
  return person.id != null ? `${name} (ID- ${person.id})` : name;
};

const StockCheckScreen = ({ embedded = false }) => {
  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  const userId = userInfo?.id;

  // ── State ───────────────────────────────────────────────────────────────────
  const [scanInput, setScanInput] = useState("");
  const [scanning, setScanning] = useState(false);
  // Camera scanner (phone / tablet) — decodes the pouch barcode and feeds the
  // voucher number straight into the same lookup the keyboard path uses.
  const [scannerOpen, setScannerOpen] = useState(false);
  // The most recently scanned / searched loan, shown in the detail panel.
  const [currentLoan, setCurrentLoan] = useState(null);
  // Every scanned pouch this session — one row in the right-hand grid.
  const [scannedList, setScannedList] = useState([]);

  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // "checked" | "unchecked" — which set the Stock Report lists. Checked default.
  const [reportType, setReportType] = useState("checked");
  // Whether the embedded report is revealed below, and a bump token that forces
  // it to re-fetch after a Save/Reset changes the underlying data.
  const [showReport, setShowReport] = useState(false);
  const [reportRefreshKey, setReportRefreshKey] = useState(0);

  // Persistent reminders derived from the DB: the most recent stock check, and
  // the most recent reset (recorded in stock_reset_date / stock_reset_by).
  const [lastCheck, setLastCheck] = useState(null);
  const [lastReset, setLastReset] = useState(null);
  const [counts, setCounts] = useState({ checkedCount: 0, uncheckedCount: 0 });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const showSnackbar = useCallback(
    (message, severity = "info") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  const scanRef = useRef(null);
  const scannerOpenRef = useRef(false);
  useEffect(() => {
    scannerOpenRef.current = scannerOpen;
  }, [scannerOpen]);
  const focusScan = useCallback(() => {
    // Keep the field focused so an RFID/barcode scanner (which types + Enter)
    // always lands its input here. While the camera dialog is up, refocusing
    // would pop the on-screen keyboard over the viewfinder — so don't.
    if (scannerOpenRef.current) return;
    if (scanRef.current) scanRef.current.focus();
  }, []);

  // ── Load the persistent "last stock checked" reminder + counts ───────────────
  const loadStatus = useCallback(async () => {
    if (storeId == null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/stock-check/status/${storeId}`,
      );
      if (res.data?.success) {
        const d = res.data.data || {};
        setLastCheck(d.lastCheck || null);
        setLastReset(d.lastReset || null);
        setCounts({
          checkedCount: d.checkedCount || 0,
          uncheckedCount: d.uncheckedCount || 0,
        });
      }
    } catch {
      // Non-fatal: the reminder chip just won't show.
    }
  }, [storeId]);

  useEffect(() => {
    loadStatus();
    focusScan();
  }, [loadStatus, focusScan]);

  // Mirror of the scanned list so a lookup can test for duplicates without
  // reaching into a state updater (the camera path needs the verdict back).
  const scannedListRef = useRef([]);
  useEffect(() => {
    scannedListRef.current = scannedList;
  }, [scannedList]);

  // ── Scan / search a pouch → load its loan + append to the grid ──────────────
  // Accepts an explicit voucher (camera scan) and otherwise falls back to what
  // is typed in the search box. Resolves to { ok, message } for the scanner feed.
  const handleScan = useCallback(
    async (voucherArg) => {
      const voucher = (
        typeof voucherArg === "string" ? voucherArg : scanInput
      ).trim();
      if (!voucher) return { ok: false, message: "Nothing to look up." };
      if (storeId == null) {
        showSnackbar("No store selected.", "warning");
        return { ok: false, message: "No store selected." };
      }
      setScanning(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
            voucher,
          )}`,
          { params: { store_id: storeId } },
        );
        if (!(res.data?.success && res.data?.statusCode === 200)) {
          const message =
            res.data?.statusMessage || `No loan found for "${voucher}".`;
          showSnackbar(message, "error");
          return { ok: false, message };
        }
        const data = res.data.data || {};
        const loanInfo = data.loan_info || {};
        const customer = data.customer_info || {};
        const ornaments = data.ornaments_info || [];

        const itemName =
          [
            ...new Set(
              ornaments.map((o) => (o.item_name || "").trim()).filter(Boolean),
            ),
          ].join(", ") || "—";
        const gross = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_gross) || 0),
          0,
        );

        const loan = {
          voucherNo: loanInfo.voucher_no || voucher,
          custId: customer.id ?? loanInfo.cust_id ?? null,
          custName: customer.name || "—",
          mobile: customer.mobile || "—",
          address: customer.pickup_place || customer.pickup_address || "—",
          loanDate: loanInfo.transaction_date || null,
          collectByDate: loanInfo.collect_by_date || null,
          loanAmount: loanInfo.loan_amount,
          interest: loanInfo.loan_interest,
          itemName,
          gross,
          stockCheckDate: loanInfo.stock_check_date || null,
        };

        setCurrentLoan(loan);

        // Dedupe by voucher number — scanning the same pouch twice shouldn't add
        // a second grid row.
        const duplicate = scannedListRef.current.some(
          (r) => r.voucherNo === loan.voucherNo,
        );
        if (duplicate) {
          showSnackbar(
            `Pouch ${loan.voucherNo} is already in the list.`,
            "info",
          );
        } else {
          setScannedList((prev) => [
            ...prev,
            {
              voucherNo: loan.voucherNo,
              loanDate: loan.loanDate,
              custName: loan.custName,
              alreadyChecked: !!loan.stockCheckDate,
              // Keep the whole loan on the row so clicking it later re-shows the
              // detail panel without another round trip.
              loan,
            },
          ]);
        }

        setScanInput("");
        return {
          ok: true,
          message: duplicate ? "Already in the list" : loan.custName,
        };
      } catch (err) {
        const message =
          err?.response?.data?.statusMessage || "Failed to look up the loan.";
        showSnackbar(message, "error");
        return { ok: false, message };
      } finally {
        setScanning(false);
        focusScan();
      }
    },
    [scanInput, storeId, showSnackbar, focusScan],
  );

  // Camera hit → drop the decoded voucher into the search box and run exactly
  // the same lookup the keyboard path runs.
  const handleCameraDetected = useCallback(
    (code) => {
      setScanInput(code);
      return handleScan(code);
    },
    [handleScan],
  );

  const removeRow = (voucherNo) => {
    setScannedList((prev) => prev.filter((r) => r.voucherNo !== voucherNo));
    // The detail panel mirrors a row in the grid — once that row is gone its
    // customer must not linger on screen.
    setCurrentLoan((cur) => (cur?.voucherNo === voucherNo ? null : cur));
  };

  // Clicking a scanned row brings its customer + loan back into the panel, so
  // the whole session stays reviewable and not just the last scan.
  const selectRow = (row) => {
    if (row?.loan) setCurrentLoan(row.loan);
  };

  const clearAll = () => {
    setScannedList([]);
    setCurrentLoan(null);
    focusScan();
  };

  // ── Save Stock Check ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (scannedList.length === 0) {
      showSnackbar("Scan at least one pouch before saving.", "warning");
      return;
    }
    if (storeId == null || userId == null) {
      showSnackbar("Missing store or user context.", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/stock-check/save`,
        {
          store_id: storeId,
          user_id: userId,
          voucher_nos: scannedList.map((r) => r.voucherNo),
        },
      );
      if (!res.data?.success) {
        showSnackbar(
          res.data?.statusMessage || "Failed to save the stock check.",
          "error",
        );
        return;
      }
      const d = res.data.data || {};
      // Reflect the just-saved check as the "last checked" reminder, then clear
      // the working area.
      setLastCheck({
        stock_check_date: d.stock_check_date,
        stock_check_by: d.stock_check_by,
      });
      setScannedList([]);
      setCurrentLoan(null);
      showSnackbar(`Stock check saved for ${d.count} pouch(es).`, "success");
      loadStatus();
      // Refresh the embedded report if it's open — the checked set just changed.
      setReportRefreshKey((k) => k + 1);
      focusScan();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage || "Failed to save the stock check.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Reset Stock Check (clears the stamp on every checked loan in the store) ──
  const handleReset = async () => {
    setResetConfirmOpen(false);
    if (storeId == null || userId == null) {
      showSnackbar("Missing store or user context.", "error");
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/stock-check/reset`,
        { store_id: storeId, user_id: userId },
      );
      if (!res.data?.success) {
        showSnackbar(
          res.data?.statusMessage || "Failed to reset the stock check.",
          "error",
        );
        return;
      }
      const d = res.data.data || {};
      // Every check stamp is cleared; record the reset (persisted in
      // stock_reset_date / stock_reset_by) as the new reminder.
      setLastCheck(null);
      setLastReset({
        stock_reset_date: d.reset_at,
        stock_reset_by: d.reset_by,
      });
      showSnackbar(`Stock check reset for ${d.count} pouch(es).`, "success");
      loadStatus();
      // Refresh the embedded report if it's open — everything is now unchecked.
      setReportRefreshKey((k) => k + 1);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage ||
          "Failed to reset the stock check.",
        "error",
      );
    } finally {
      setResetting(false);
    }
  };

  // ── Stock Report → reveal the embedded Receipt / Member Register below ───────
  // Reuses the full Receipt Report (search, PDF export, register table) rendered
  // in place, filtered to the Checked / Not-Checked set chosen above.
  const handleReport = () => {
    setShowReport(true);
    setReportRefreshKey((k) => k + 1);
  };

  // ── Detail-panel row helper ──────────────────────────────────────────────────
  const DetailField = ({ label, value, strong }) => (
    <Grid item xs={12} sm={6}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: INK_MUTED,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "0.95rem" : "0.85rem",
          fontWeight: strong ? 800 : 600,
          color: strong ? INK : INK_SOFT,
          wordBreak: "break-word",
        }}
      >
        {value == null || value === "" ? "—" : value}
      </Typography>
    </Grid>
  );

  // Only the *latest* of the two persistent reminders is meaningful: a store
  // can carry both a stock_check and a later reset (or vice-versa) in the DB,
  // but showing both is ambiguous. Compare timestamps and keep the newer one.
  const activeReminder = (() => {
    const checkAt = lastCheck?.stock_check_date
      ? dayjs(lastCheck.stock_check_date)
      : null;
    const resetAt = lastReset?.stock_reset_date
      ? dayjs(lastReset.stock_reset_date)
      : null;
    if (checkAt?.isValid() && resetAt?.isValid())
      return resetAt.isAfter(checkAt) ? "reset" : "check";
    if (checkAt?.isValid()) return "check";
    if (resetAt?.isValid()) return "reset";
    return null;
  })();

  const headCellSx = {
    bgcolor: "#F8FAFC",
    color: INK_MUTED,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: "nowrap",
  };
  const cellSx = {
    fontSize: "0.8rem",
    color: INK_SOFT,
    borderBottom: `1px solid ${BORDER}`,
    py: 1,
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* Heading */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { md: "row", xs: "column" },
          ...reportFixedSx,
        }}
      >
        {!embedded && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PawnNavButtons />
            <Box
              sx={{
                width: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: BLUE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
              }}
            >
              <StockIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.05rem", sm: "1.25rem" },
                  color: INK,
                }}
              >
                Stock Check
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
                {currentStoreInfo?.name || ""}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Body — with the register open the working panels collapse into a ~30%
          rail down the left (scan → scanned pouches → customer & loan details,
          top to bottom) and the register takes the remaining ~70%, so it gets a
          full column of height instead of the sliver left under a three-across
          row. With the register hidden there is nothing to share the width
          with, so the same three panels spread back across the full width.
          Below lg they are stacked either way — at md (900px) three columns are
          225 / 375 / 300px slivers. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 1.5,
          // Only the register needs the leftover height; with it hidden the
          // panels keep their natural height as before.
          flex: showReport ? 1 : "none",
          minHeight: 0,
        }}
      >
        {/* Workspace: scan · scanned pouches · customer & loan details */}
        <Box
          sx={{
            width: { xs: "100%", lg: showReport ? "30%" : "100%" },
            flexShrink: 0,
            minWidth: 0,
            minHeight: 0,
            // Stacked into a narrow rail the three panels can outrun the
            // viewport — the rail scrolls on its own rather than pushing the
            // page (scrollbar hidden, as everywhere else on the report pages).
            overflowY: { lg: showReport ? "auto" : "visible" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Grid container spacing={1}>
            {/* Search + Reminder + Report Generate Buttons */}
            <Grid item xs={12} lg={showReport ? 12 : 3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                }}
              >
                {/* Last Checked / Reset reminder — only the most recent action */}
                {activeReminder === "check" && (
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: 18 }} />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.78rem" }}>
                          Last checked-{" "}
                          <span style={{ fontWeight: 600 }}>
                            {fmtDateTime(lastCheck.stock_check_date)}
                          </span>
                        </Typography>
                        <Typography sx={{ fontSize: "0.78rem" }}>
                          By-{" "}
                          <span style={{ fontWeight: 600 }}>
                            {personLabel(lastCheck.stock_check_by)}
                          </span>
                        </Typography>
                      </Box>
                    }
                    sx={{
                      bgcolor: SUCCESS_PALE,
                      color: SUCCESS,
                      fontWeight: 700,
                      py: 2,
                      px: 0.5,
                      "& .MuiChip-icon": { color: SUCCESS },
                      height: "auto",
                      borderRadius: 2,
                      width: "100%",
                    }}
                  />
                )}
                {activeReminder === "reset" && (
                  <Chip
                    icon={<ResetIcon sx={{ fontSize: 18 }} />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.78rem" }}>
                          Stock check reset-{" "}
                          <span style={{ fontWeight: 600 }}>
                            {fmtDateTime(lastReset.stock_reset_date)}
                          </span>
                        </Typography>
                        <Typography sx={{ fontSize: "0.78rem" }}>
                          By-{" "}
                          <span style={{ fontWeight: 600 }}>
                            {personLabel(lastReset.stock_reset_by)}
                          </span>
                        </Typography>
                      </Box>
                    }
                    sx={{
                      bgcolor: "#FEF3C7",
                      color: AMBER,
                      fontWeight: 700,
                      py: 2,
                      px: 0.5,
                      "& .MuiChip-icon": { color: AMBER },
                      height: "auto",
                      borderRadius: 2,
                      width: "100%",
                    }}
                  />
                )}

                {/* Search */}
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    bgcolor: SURFACE,
                  }}
                >
                  <CardContent
                    sx={{
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 1.5, md: 2 },
                      "&:last-child": { pb: { xs: 1.5, md: 2 } },
                    }}
                  >
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} md>
                        <TextField
                          inputRef={scanRef}
                          label="Scan barcode / search pledge no."
                          placeholder="Scan a pouch or type a voucher number, then Enter"
                          size="small"
                          fullWidth
                          autoFocus
                          value={scanInput}
                          onChange={(e) => setScanInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleScan()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon
                                  sx={{ fontSize: 20, color: INK_MUTED }}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                {scanning && (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: BLUE, mr: 0.75 }}
                                  />
                                )}
                                {/* Camera scan — the whole point of running this
                                screen on a phone or tablet. */}
                                <Tooltip title="Scan with camera">
                                  <IconButton
                                    size="small"
                                    onClick={() => setScannerOpen(true)}
                                    sx={{
                                      bgcolor: BLUE,
                                      color: "#fff",
                                      borderRadius: 1.5,
                                      p: 0.6,
                                      "&:hover": { bgcolor: BLUE_DARK },
                                    }}
                                  >
                                    <ScanIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Button
                          variant="contained"
                          onClick={handleScan}
                          disabled={scanning || !scanInput.trim()}
                          startIcon={<SearchIcon />}
                          sx={{
                            bgcolor: BLUE,
                            textTransform: "none",
                            fontWeight: 700,
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: BLUE_DARK,
                              boxShadow: "none",
                            },
                          }}
                        >
                          Load
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Report & Reset Buttons */}
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    bgcolor: SURFACE,
                  }}
                >
                  <CardContent
                    sx={{
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 1.5, md: 2 },
                      "&:last-child": { pb: { xs: 1.5, md: 2 } },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <RadioGroup
                        row
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                      >
                        <FormControlLabel
                          value="checked"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: BLUE,
                                "&.Mui-checked": { color: BLUE },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: "0.85rem", color: INK_SOFT }}
                            >
                              Checked
                              <Box
                                component="span"
                                sx={{ color: INK_MUTED, ml: 0.5 }}
                              >
                                ({counts.checkedCount})
                              </Box>
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="unchecked"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: BLUE,
                                "&.Mui-checked": { color: BLUE },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: "0.85rem", color: INK_SOFT }}
                            >
                              Not Checked
                              <Box
                                component="span"
                                sx={{ color: INK_MUTED, ml: 0.5 }}
                              >
                                ({counts.uncheckedCount})
                              </Box>
                            </Typography>
                          }
                        />
                      </RadioGroup>

                      {/* Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Button
                          variant={showReport ? "contained" : "outlined"}
                          onClick={() =>
                            showReport ? setShowReport(false) : handleReport()
                          }
                          startIcon={<ReportIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            ...(showReport
                              ? {
                                  bgcolor: BLUE,
                                  color: "#fff",
                                  boxShadow: "none",
                                  "&:hover": {
                                    bgcolor: BLUE_DARK,
                                    boxShadow: "none",
                                  },
                                }
                              : {
                                  color: BLUE,
                                  borderColor: BLUE,
                                  "&:hover": {
                                    borderColor: BLUE_DARK,
                                    bgcolor: BLUE_PALE,
                                  },
                                }),
                          }}
                        >
                          {showReport ? "Hide Report" : "Stock Report"}
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setResetConfirmOpen(true)}
                          disabled={resetting}
                          startIcon={
                            resetting ? (
                              <CircularProgress size={16} color="error" />
                            ) : (
                              <ResetIcon />
                            )
                          }
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Reset Stock Check
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Scanned grid */}
            <Grid item xs={12} lg={showReport ? 12 : 4}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 3,
                  bgcolor: SURFACE,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Heading */}
                <Box
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    borderBottom: `1px solid ${BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 700, color: INK, fontSize: 16 }}
                  >
                    Scanned Pouches
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${scannedList.length}`}
                      size="small"
                      sx={{
                        bgcolor: BLUE_PALE,
                        color: BLUE_DARK,
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    />
                    {scannedList.length > 0 && (
                      <Tooltip title="Clear list">
                        <IconButton size="small" onClick={clearAll}>
                          <ClearIcon sx={{ fontSize: 14, color: INK_MUTED }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                {/* Scanned Pouches Table */}
                <TableContainer
                  sx={{
                    // Across the row a fixed height keeps the three cards level;
                    // in the rail it only takes what its rows need (capped), so
                    // an empty list doesn't push the detail card off screen.
                    ...(showReport ? { maxHeight: 190 } : { height: 215 }),
                    overflow: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      width: 0,
                      display: "none",
                    },
                    msOverflowStyle: "none",
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Sl No</TableCell>
                        <TableCell sx={headCellSx}>Date</TableCell>
                        <TableCell sx={headCellSx}>Voucher</TableCell>
                        <TableCell sx={headCellSx} align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scannedList.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{ ...cellSx, textAlign: "center", py: 5 }}
                          >
                            <Typography
                              sx={{ color: INK_MUTED, fontSize: "0.85rem" }}
                            >
                              No pouches scanned yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        scannedList.map((r, idx) => (
                          <TableRow
                            key={r.voucherNo}
                            hover
                            onClick={() => selectRow(r)}
                            sx={{
                              cursor: "pointer",
                              bgcolor:
                                currentLoan?.voucherNo === r.voucherNo
                                  ? BLUE_PALE
                                  : "transparent",
                              "&:hover": { bgcolor: BLUE_PALE },
                            }}
                          >
                            <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                              {idx + 1}
                            </TableCell>
                            <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                              {fmtDate(r.loanDate)}
                            </TableCell>
                            <TableCell
                              sx={{ ...cellSx, fontWeight: 700, color: INK }}
                            >
                              {r.voucherNo}
                              {r.alreadyChecked && (
                                <Tooltip title="Was already stock-checked">
                                  <CheckIcon
                                    sx={{
                                      fontSize: 14,
                                      color: SUCCESS,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...cellSx, p: 0.5 }} align="right">
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeRow(r.voucherNo);
                                  }}
                                >
                                  <DeleteIcon
                                    sx={{ fontSize: 17, color: INK_MUTED }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Save action for the scanned set */}
                <Box sx={{ p: 1.5, borderTop: `1px solid ${BORDER}` }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || scannedList.length === 0}
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      bgcolor: SUCCESS,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#047857", boxShadow: "none" },
                    }}
                  >
                    Save Stock Check
                  </Button>
                </Box>
              </Card>
            </Grid>

            {/* Detail panel */}
            <Grid item xs={12} lg={showReport ? 12 : 5}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 3,
                  bgcolor: SURFACE,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    borderBottom: `1px solid ${BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 22, color: INK_MUTED }} />
                  <Typography
                    sx={{ fontWeight: 700, color: INK, fontSize: 16 }}
                  >
                    Customer & Loan Details
                  </Typography>
                </Box>
                {currentLoan ? (
                  <CardContent sx={{ py: 2 }}>
                    {/* In the narrow rail the two halves would be 180px each, so
                    they stack; across the full width they sit side by side. */}
                    <Grid container spacing={2}>
                      {/* Customer Details */}
                      <Grid item xs={12} md={showReport ? 12 : 6}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: INK_MUTED, mb: 1 }}
                        >
                          Customer Details
                        </Typography>
                        <Grid container spacing={1}>
                          <DetailField
                            label="Customer Name"
                            value={currentLoan.custName}
                            strong
                          />
                          <DetailField
                            label="Cust ID"
                            value={currentLoan.custId}
                          />
                          <DetailField
                            label="Mobile"
                            value={currentLoan.mobile}
                          />
                          <DetailField
                            label="Address"
                            value={currentLoan.address}
                          />
                        </Grid>
                      </Grid>

                      {/* Loan Details */}
                      <Grid item xs={12} md={showReport ? 12 : 6}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: INK_MUTED, mb: 1 }}
                        >
                          Loan Details
                        </Typography>
                        <Grid container spacing={1}>
                          <DetailField
                            label="Pledge / Voucher No"
                            value={currentLoan.voucherNo}
                            strong
                          />
                          <DetailField
                            label="Loan Date"
                            value={fmtDate(currentLoan.loanDate)}
                          />
                          <DetailField
                            label="Loan Amount"
                            value={`₹ ${fmtINR(currentLoan.loanAmount)}`}
                          />
                          <DetailField
                            label="Interest"
                            value={
                              currentLoan.interest == null
                                ? "—"
                                : `${currentLoan.interest}%`
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {currentLoan.stockCheckDate && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: SUCCESS_PALE,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 16, color: SUCCESS }} />
                        <Typography
                          sx={{ fontSize: "0.75rem", color: SUCCESS }}
                        >
                          Already stock-checked on{" "}
                          {fmtDateTime(currentLoan.stockCheckDate)}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                    <ScanIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
                    <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
                      Scan a pouch or search a voucher number to load its
                      details — or tap any scanned row to bring it back here.
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* ── Embedded Stock Report (full Receipt / Member Register) ── */}
        {showReport && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              // Below lg it sits under the rail and needs a floor of its own;
              // beside the rail it just takes the column height.
              minHeight: { xs: 400, lg: 0 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReceiptReport
              embedded
              stockFilter={reportType}
              key={`${reportType}-${reportRefreshKey}`}
            />
          </Box>
        )}
      </Box>

      {/* ── Camera barcode scanner (phone / tablet stock check) ──
          Closing deliberately does not refocus the search box: on a phone that
          would pop the soft keyboard the moment the viewfinder disappears. */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleCameraDetected}
        title="Scan Pouch Barcode"
        hint="Point the camera at the pouch barcode — each pledge loads and is added below. Keep scanning, then tap Done and Save."
      />

      {/* ── Reset confirmation ── */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: INK }}>
          Reset stock check?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", color: INK_SOFT }}>
            This clears the stock-check stamp on <b>every</b> loan in{" "}
            {currentStoreInfo?.name || "this store"}
            {counts.checkedCount
              ? ` (${counts.checkedCount} currently checked)`
              : ""}
            . This cannot be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResetConfirmOpen(false)}
            sx={{ textTransform: "none", color: INK_MUTED }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReset}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Reset all
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockCheckScreen;
